"use client"

// packages/frontend/src/components/ui/reactbits/BounceWrapper.tsx

import { type ReactNode } from "react"
import { motion, type Variants } from "framer-motion"
import { cn } from "@/utils/cn"

interface BounceWrapperProps {
  children: ReactNode
  className?: string
  disabled?: boolean
  delay?: number
}

/**
 * Wrapper for ReactBits Bounce component with theme support
 * Provides bounce entry animation for celebrations
 */
export function BounceWrapper({
  children,
  className,
  disabled = false,
  delay = 0,
}: BounceWrapperProps) {
  // If disabled, return children without animation
  if (disabled) {
    return <>{children}</>
  }

  // Try to import ReactBits Bounce, fallback to custom implementation
  let Bounce: any = null
  try {
    const reactbits = require("@appletosolutions/reactbits")
    Bounce = reactbits.Bounce || reactbits.default?.Bounce
  } catch (e) {
    // Fallback to custom implementation
  }

  // Custom bounce animation
  const bounceVariants: Variants = {
    initial: {
      scale: 0,
      opacity: 0,
      y: 50,
    },
    animate: {
      scale: [0, 1.2, 1],
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay,
        ease: [0.34, 1.56, 0.64, 1], // Custom bounce easing
      },
    },
  }

  // If ReactBits component is available, use it
  if (Bounce) {
    return (
      <Bounce className={className} disabled={disabled} delay={delay}>
        {children}
      </Bounce>
    )
  }

  // Custom implementation
  return (
    <motion.div
      className={cn(className)}
      variants={bounceVariants}
      initial="initial"
      animate="animate"
    >
      {children}
    </motion.div>
  )
}

